import React from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'

// Layout and Core Components
import Layout from '@components/layout/Layout'
import { DocumentProvider } from '@stores/documentStore'
import { EditorProvider } from '@stores/editorStore'
import { AIProvider } from '@stores/aiStore'
import { EditorSettingsProvider } from '@stores/editorSettingsStore'
import { useAuthStore } from '@stores/authStore'
import { ReviewProvider } from '@stores/reviewStore'

// Pages
import LandingPage from '@components/pages/LandingPage'
import HomePage from '@components/pages/HomePage'
import EditorPage from '@components/pages/EditorPage'
import DocumentsPage from '@components/pages/DocumentsPage'
import SettingsPage from '@components/pages/SettingsPage'
import SubscriptionPage from '@components/pages/SubscriptionPage'
import DiscoverPage from '@components/pages/DiscoverPage'
import SearchResultsPage from '@components/pages/SearchResultsPage'
import TemplatesPage from '@components/pages/TemplatesPage'
import TagsPage from '@components/pages/TagsPage'
import LibraryPage from '@components/pages/LibraryPage'
import AdminPage from '@components/pages/AdminPage'
import LoginPage from '@components/pages/LoginPage'
import RegisterPage from '@components/pages/RegisterPage'
import ForgotPasswordPage from '@components/pages/ForgotPasswordPage'
import WorkspacePage from '@components/pages/WorkspacePage'
import { WorkspaceView } from '@components/workspaces/WorkspaceView'
import { InsufficientCreditsNotice } from '@components/tokens/InsufficientTokensNotice'

// Error handling
import ErrorBoundary from '@components/common/ErrorBoundary'
import ProtectedRoute from '@components/common/ProtectedRoute'
import AdminRoute from '@components/common/AdminRoute'


function App() {
  const { checkAuth } = useAuthStore()
  const location = window.location.pathname

  // Check authentication on app mount, but skip on public pages
  React.useEffect(() => {
    // Don't check auth on public pages (landing, login, register)
    const publicPaths = ['/', '/login', '/register']
    if (!publicPaths.includes(location)) {
      checkAuth()
    }
  }, [checkAuth, location])

  return (
    <QueryClientProvider client={queryClient}>
      <DocumentProvider>
        <EditorProvider>
          <EditorSettingsProvider>
            <AIProvider>
              <ReviewProvider>
                <Router>
                  <ErrorBoundary>
                    <div>
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <LandingPage />
                          }
                        />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                        {/* Protected routes */}
                        <Route
                          path="/*"
                          element={
                            <Layout>
                              <Routes>
                                <Route
                                  path="/home"
                                  element={
                                    <ProtectedRoute>
                                      <HomePage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/editor"
                                  element={
                                    <ProtectedRoute>
                                      <EditorPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/editor/:documentId"
                                  element={
                                    <ProtectedRoute>
                                      <EditorPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/documents"
                                  element={
                                    <ProtectedRoute>
                                      <DocumentsPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/settings"
                                  element={
                                    <ProtectedRoute>
                                      <SettingsPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/subscription"
                                  element={
                                    <ProtectedRoute>
                                      <SubscriptionPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/discover"
                                  element={
                                    <ProtectedRoute>
                                      <DiscoverPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/discover/search"
                                  element={
                                    <ProtectedRoute>
                                      <SearchResultsPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/library"
                                  element={
                                    <ProtectedRoute>
                                      <LibraryPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/workspaces"
                                  element={
                                    <ProtectedRoute>
                                      <WorkspacePage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/workspaces/:workspaceId"
                                  element={
                                    <ProtectedRoute>
                                      <WorkspaceView />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/discover/templates"
                                  element={
                                    <ProtectedRoute>
                                      <TemplatesPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/templates"
                                  element={
                                    <ProtectedRoute>
                                      <TemplatesPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/tags"
                                  element={
                                    <ProtectedRoute>
                                      <TagsPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin"
                                  element={
                                    <AdminRoute>
                                      <AdminPage />
                                    </AdminRoute>
                                  }
                                />
                                {/* Catch all route */}
                                <Route
                                  path="*"
                                  element={
                                    <ProtectedRoute>
                                      <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                                            Page Not Found
                                          </h1>
                                          <p className="text-gray-600 mb-4">
                                            The page you're looking for doesn't exist.
                                          </p>
                                          <a
                                            href="/"
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                          >
                                            Go to Home
                                          </a>
                                        </div>
                                      </div>
                                    </ProtectedRoute>
                                  }
                                />
                              </Routes>
                            </Layout>
                          }
                        />
                      </Routes>
                    </div>
                  </ErrorBoundary>
                </Router>
              </ReviewProvider>
            </AIProvider>
          </EditorSettingsProvider>
        </EditorProvider>
      </DocumentProvider>
    </QueryClientProvider>
  )
}

export default App